import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import examsService from '../../services/examsService';
import classesService from '../../services/classesService';
import staffService from '../../services/staffService';
import schoolsService from '../../services/schoolsService';
import DialogModal from '../../components/DialogModal';
import { Plus, Trash, Armchair, UserCheck, CaretLeft } from '@phosphor-icons/react';

const emptySessionForm = { name: '', academic_term_id: '', start_date: '', end_date: '' };
const emptyScheduleForm = { class_id: '', subject_id: '', exam_date: '', start_time: '', end_time: '', room: '', max_marks: 100 };

function ExamSchedulingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);

  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionForm, setSessionForm] = useState(emptySessionForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteSessionTarget, setDeleteSessionTarget] = useState(null);

  const [selectedSession, setSelectedSession] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm);

  const [seatingModal, setSeatingModal] = useState(null); // { schedule, seats }
  const [invigilatorModal, setInvigilatorModal] = useState(null); // { schedule, invigilators }
  const [pickedStaffId, setPickedStaffId] = useState('');

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await examsService.listSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load exam sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
    classesService.listClasses().then(d => setClasses(d || [])).catch(() => {});
    classesService.listAllSubjects().then(d => setSubjects(Array.isArray(d) ? d : [])).catch(() => {});
    staffService.listStaff().then(d => setStaff(d?.items || [])).catch(() => {});
  }, [loadSessions]);

  useEffect(() => {
    if (!user?.school_id) return;
    schoolsService.listTerms(user.school_id).then(data => {
      const list = data || [];
      setTerms(list);
      const current = list.find(t => t.is_current);
      setSessionForm(prev => ({ ...prev, academic_term_id: prev.academic_term_id || (current ? current.id : (list[0]?.id || '')) }));
    }).catch(() => {});
  }, [user]);

  const openSessionModal = () => {
    setSessionForm({ ...emptySessionForm, academic_term_id: sessionForm.academic_term_id });
    setShowSessionModal(true);
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (!sessionForm.name || !sessionForm.academic_term_id || !sessionForm.start_date || !sessionForm.end_date) {
      toast.error('All fields are required');
      return;
    }
    setSubmitting(true);
    try {
      await examsService.createSession(sessionForm);
      toast.success('Exam session created');
      setShowSessionModal(false);
      loadSessions();
    } catch (error) {
      toast.error(error.message || 'Failed to create exam session');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteSession = async () => {
    if (!deleteSessionTarget) return;
    try {
      await examsService.deleteSession(deleteSessionTarget);
      toast.success('Exam session deleted');
      setDeleteSessionTarget(null);
      if (selectedSession?.id === deleteSessionTarget) setSelectedSession(null);
      loadSessions();
    } catch (error) {
      toast.error('Failed to delete exam session');
    }
  };

  const openSession = async (session) => {
    try {
      const detail = await examsService.getSession(session.id);
      setSelectedSession(detail);
    } catch (error) {
      toast.error('Failed to load session details');
    }
  };

  const refreshSelectedSession = async () => {
    if (!selectedSession) return;
    const detail = await examsService.getSession(selectedSession.id);
    setSelectedSession(detail);
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleForm.class_id || !scheduleForm.subject_id || !scheduleForm.exam_date || !scheduleForm.start_time || !scheduleForm.end_time) {
      toast.error('Class, subject, date, and times are required');
      return;
    }
    setSubmitting(true);
    try {
      await examsService.createSchedule(selectedSession.id, { ...scheduleForm, max_marks: Number(scheduleForm.max_marks) });
      toast.success('Exam scheduled');
      setShowScheduleModal(false);
      setScheduleForm(emptyScheduleForm);
      refreshSelectedSession();
    } catch (error) {
      toast.error(error.message || 'Failed to schedule exam — check for room/class conflicts');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await examsService.deleteSchedule(scheduleId);
      toast.success('Exam schedule removed');
      refreshSelectedSession();
    } catch (error) {
      toast.error('Failed to remove schedule');
    }
  };

  const openSeating = async (schedule) => {
    try {
      const seats = await examsService.getSeating(schedule.id);
      setSeatingModal({ schedule, seats: Array.isArray(seats) ? seats : [] });
    } catch (error) {
      toast.error('Failed to load seating');
    }
  };

  const handleGenerateSeating = async (schedule) => {
    try {
      const result = await examsService.generateSeating(schedule.id);
      toast.success(`Assigned ${result.seats_assigned} seats in ${result.room}`);
      openSeating(schedule);
    } catch (error) {
      toast.error(error.message || 'Failed to generate seating');
    }
  };

  const openInvigilators = async (schedule) => {
    try {
      const invigilators = await examsService.listInvigilators(schedule.id);
      setInvigilatorModal({ schedule, invigilators: Array.isArray(invigilators) ? invigilators : [] });
      setPickedStaffId('');
    } catch (error) {
      toast.error('Failed to load invigilators');
    }
  };

  const handleAssignInvigilator = async () => {
    if (!pickedStaffId) {
      toast.error('Select a staff member');
      return;
    }
    try {
      await examsService.assignInvigilator(invigilatorModal.schedule.id, { staff_id: pickedStaffId });
      toast.success('Invigilator assigned');
      openInvigilators(invigilatorModal.schedule);
    } catch (error) {
      toast.error(error.message || 'Failed to assign invigilator');
    }
  };

  const handleRemoveInvigilator = async (id) => {
    try {
      await examsService.removeInvigilator(id);
      toast.success('Invigilator removed');
      openInvigilators(invigilatorModal.schedule);
    } catch (error) {
      toast.error('Failed to remove invigilator');
    }
  };

  const className = (id) => classes.find(c => c.id === id)?.name || id;
  const subjectName = (id) => subjects.find(s => s.id === id)?.name || id;

  if (loading) {
    return <Layout><div className="loading">Loading exam sessions...</div></Layout>;
  }

  if (selectedSession) {
    return (
      <Layout>
        <div className="page">
          <div className="page-header">
            <div>
              <button className="btn btn-secondary" onClick={() => setSelectedSession(null)} style={{ marginBottom: 8 }}>
                <CaretLeft size={16} /> Back to Sessions
              </button>
              <h1>{selectedSession.name}</h1>
              <p>{selectedSession.start_date} to {selectedSession.end_date} · {selectedSession.schedule_count} exam(s) scheduled</p>
            </div>
            <button className="btn btn-primary" onClick={() => setShowScheduleModal(true)}>
              <Plus size={20} /> Schedule Exam
            </button>
          </div>

          <div className="table-container">
            {selectedSession.schedules.length === 0 ? (
              <div className="empty-state"><p>No exams scheduled yet for this session</p></div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Subject</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Room</th>
                    <th>Max Marks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSession.schedules.map(s => (
                    <tr key={s.id}>
                      <td>{className(s.class_id)}</td>
                      <td>{subjectName(s.subject_id)}</td>
                      <td>{s.exam_date}</td>
                      <td>{s.start_time} - {s.end_time}</td>
                      <td>{s.room || '-'}</td>
                      <td>{s.max_marks}</td>
                      <td className="action-buttons">
                        <button className="btn-icon" title="Generate/View Seating" onClick={() => handleGenerateSeating(s)}>
                          <Armchair size={18} />
                        </button>
                        <button className="btn-icon" title="Invigilators" onClick={() => openInvigilators(s)}>
                          <UserCheck size={18} />
                        </button>
                        <button className="btn-icon btn-danger" title="Remove" onClick={() => handleDeleteSchedule(s.id)}>
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {showScheduleModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Schedule Exam</h2>
                <button type="button" className="btn-close" onClick={() => setShowScheduleModal(false)} aria-label="Close">×</button>
              </div>
              <form onSubmit={handleCreateSchedule}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Class *</label>
                    <select value={scheduleForm.class_id} onChange={e => setScheduleForm({ ...scheduleForm, class_id: e.target.value })} required>
                      <option value="">Select class</option>
                      {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Subject *</label>
                    <select value={scheduleForm.subject_id} onChange={e => setScheduleForm({ ...scheduleForm, subject_id: e.target.value })} required>
                      <option value="">Select subject</option>
                      {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Exam Date *</label>
                    <input type="date" value={scheduleForm.exam_date} onChange={e => setScheduleForm({ ...scheduleForm, exam_date: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Room</label>
                    <input type="text" value={scheduleForm.room} onChange={e => setScheduleForm({ ...scheduleForm, room: e.target.value })} placeholder="e.g. Hall A" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Time *</label>
                    <input type="time" value={scheduleForm.start_time} onChange={e => setScheduleForm({ ...scheduleForm, start_time: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>End Time *</label>
                    <input type="time" value={scheduleForm.end_time} onChange={e => setScheduleForm({ ...scheduleForm, end_time: e.target.value })} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Max Marks</label>
                  <input type="number" min="1" value={scheduleForm.max_marks} onChange={e => setScheduleForm({ ...scheduleForm, max_marks: e.target.value })} />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Schedule'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowScheduleModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {seatingModal && (
          <div className="modal-overlay" onClick={() => setSeatingModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Seating — {className(seatingModal.schedule.class_id)} / {subjectName(seatingModal.schedule.subject_id)}</h2>
                <button type="button" className="btn-close" onClick={() => setSeatingModal(null)} aria-label="Close">×</button>
              </div>
              {seatingModal.seats.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No seats generated yet.</p>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <table className="data-table">
                    <thead><tr><th>Seat</th><th>Student</th><th>ID</th></tr></thead>
                    <tbody>
                      {seatingModal.seats.map(seat => (
                        <tr key={seat.id}>
                          <td>{seat.room} - {seat.seat_number}</td>
                          <td>{seat.student_name}</td>
                          <td>{seat.student_id_code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="form-actions">
                <button className="btn btn-primary" onClick={() => handleGenerateSeating(seatingModal.schedule)}>
                  Regenerate Seating
                </button>
              </div>
            </div>
          </div>
        )}

        {invigilatorModal && (
          <div className="modal-overlay" onClick={() => setInvigilatorModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Invigilators — {className(invigilatorModal.schedule.class_id)} / {subjectName(invigilatorModal.schedule.subject_id)}</h2>
                <button type="button" className="btn-close" onClick={() => setInvigilatorModal(null)} aria-label="Close">×</button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <select value={pickedStaffId} onChange={e => setPickedStaffId(e.target.value)} style={{ flex: 1 }}>
                  <option value="">Select staff member...</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
                <button className="btn btn-primary" onClick={handleAssignInvigilator}>Assign</button>
              </div>
              {invigilatorModal.invigilators.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No invigilators assigned yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {invigilatorModal.invigilators.map(inv => (
                    <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #E5E5E0', borderRadius: 8, padding: '8px 12px' }}>
                      <span>{inv.staff_name}</span>
                      <button className="btn-icon btn-danger" onClick={() => handleRemoveInvigilator(inv.id)}>
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Exam Scheduling</h1>
            <p>Schedule internal exams with room/class conflict checking, auto-generated seating, and invigilator assignment</p>
          </div>
          <button className="btn btn-primary" onClick={openSessionModal}>
            <Plus size={20} /> New Exam Session
          </button>
        </div>

        <div className="table-container">
          {sessions.length === 0 ? (
            <div className="empty-state"><p>No exam sessions yet — create one to start scheduling exams</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => openSession(s)}>
                    <td>{s.name}</td>
                    <td>{s.start_date} to {s.end_date}</td>
                    <td><span className="badge">{s.status}</span></td>
                    <td className="action-buttons" onClick={e => e.stopPropagation()}>
                      <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteSessionTarget(s.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showSessionModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>New Exam Session</h2>
              <button type="button" className="btn-close" onClick={() => setShowSessionModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleCreateSession}>
              <div className="form-group">
                <label>Name *</label>
                <input type="text" value={sessionForm.name} onChange={e => setSessionForm({ ...sessionForm, name: e.target.value })} placeholder="e.g. Mid-Term Exams" required />
              </div>
              <div className="form-group">
                <label>Academic Term *</label>
                <select value={sessionForm.academic_term_id} onChange={e => setSessionForm({ ...sessionForm, academic_term_id: e.target.value })} required>
                  <option value="">Select a term...</option>
                  {terms.map(t => <option key={t.id} value={t.id}>{t.academic_year} - Term {t.term}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input type="date" value={sessionForm.start_date} onChange={e => setSessionForm({ ...sessionForm, start_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input type="date" value={sessionForm.end_date} onChange={e => setSessionForm({ ...sessionForm, end_date: e.target.value })} required />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowSessionModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteSessionTarget}
        onClose={() => setDeleteSessionTarget(null)}
        title="Delete Exam Session"
        message="This will permanently delete this exam session and all its schedules, seating, and invigilator assignments. Continue?"
        type="warning"
        primaryAction={{ label: 'Delete', onClick: confirmDeleteSession }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default ExamSchedulingPage;
